import {Injectable, Injector, NgZone, OnDestroy} from '@angular/core';
import {Network} from '@capacitor/network';
import {App} from '@capacitor/app';
import {PluginListenerHandle} from '@capacitor/core';
import {LangService, LoaderService} from '@satoris/ngx-satoris';
import {BehaviorSubject, Subject, Subscription} from 'rxjs';
import {debounceTime} from 'rxjs/operators';
import {getStoredItem, removeStoredItem, setStoredItem} from '../utils/storage';
import {UserValidGuardService} from './user-valid-guard.service';
import {Router} from '@angular/router';
import {ApiService} from './api.service';
import {UserPlatformRole} from '../models/user';

const RECOVERY_PING_MS = 5_000;

@Injectable({
  providedIn: 'root'
})
export class SyncService implements OnDestroy {

  isOnlineSubject = new BehaviorSubject<boolean>(false);

  get isOnline$() {
    return this.isOnlineSubject?.asObservable();
  }

  get isOnline(): boolean {
    return this.isOnlineSubject?.value;
  }

  isDataSkimRefetch = true;
  private onlineChange: Subject<void> = new Subject<void>();
  private offlineChange: Subject<void> = new Subject<void>();

  private onlineFetchSub?: Subscription;
  private retryableStreak = 0;
  private heartbeatTimer: NodeJS.Timeout;
  private pingInFlight: Promise<boolean> | null = null;
  private hasInterface = true;
  private isForeground = true;
  private networkListener: PluginListenerHandle | null = null;
  private appStateListener: PluginListenerHandle | null = null;

  constructor(private loader: LoaderService,
    private lang: LangService,
    private zone: NgZone,
    private injector: Injector,
    private router: Router) {
    this.onlineChange.pipe(debounceTime(1000)).subscribe(() => {
      this.doSomethingWhenOnline();
    });
    this.offlineChange.pipe(debounceTime(1000)).subscribe(() => {
      this.doSomethingWhenOffline();
    });
  }

  init(): Promise<void> {
    const manualOffline = getStoredItem('manualOffline');
    return Promise.all([
      Network.getStatus(),
      Network.addListener('networkStatusChange', s => {
        this.zone.run(() => {
          this.hasInterface = s.connectionType !== 'none';
          if(!this.hasInterface) {
            this.setOnline(false);
          } else if(getStoredItem('manualOffline') === undefined) {
            this.probeOnce();
          }
        });
      }),
      App.addListener('appStateChange', state => {
        this.zone.run(() => {
          this.isForeground = state.isActive;
          if(state.isActive) {
            if(getStoredItem('manualOffline') === undefined && this.hasInterface) {
              this.probeOnce();
            }
          } else {
            clearTimeout(this.heartbeatTimer);
          }
        });
      })
    ]).then(([status, netListener, appListener]) => {
      this.networkListener = netListener;
      this.appStateListener = appListener;
      this.hasInterface = status.connectionType !== 'none';
      if(manualOffline !== undefined || !this.hasInterface) {
        this.setOnline(false);
        return;
      }
      return this.probeOnce().then(() => undefined);
    });
  }

  changeOnline(isOnline: boolean) {
    this.zone.run(() => this.setOnline(isOnline));
  }

  private setOnline(isOnline: boolean) {
    if(this.isOnlineSubject.value !== isOnline) {
      this.isOnlineSubject.next(isOnline);
      if(isOnline) {
        this.onlineChange.next();
      } else {
        this.isDataSkimRefetch = false;
        this.offlineChange.next();
      }
    }
    this.scheduleHeartbeat();
  }

  private probeOnce(): Promise<boolean> {
    if(this.pingInFlight) return this.pingInFlight;
    if(!this.hasInterface) {
      this.setOnline(false);
      return Promise.resolve(false);
    }
    const api = this.injector.get(ApiService);
    this.pingInFlight = api.ping().then(res => {
      const ok = res === 'pong';
      this.zone.run(() => {
        if(ok) removeStoredItem('manualOffline');
        this.setOnline(ok);
      });
      return ok;
    }).catch(() => {
      this.zone.run(() => this.setOnline(false));
      return false;
    }).finally(() => {
      this.pingInFlight = null;
    });
    return this.pingInFlight;
  }

  setManualOffline() {
    setStoredItem('manualOffline', true);
    this.zone.run(() => this.setOnline(false));
  }

  private scheduleHeartbeat() {
    clearTimeout(this.heartbeatTimer);
    if(this.isOnlineSubject.value) return;
    if(!this.hasInterface) return;
    if(!this.isForeground) return;
    const delay = this.retryableStreak > 2 ? RECOVERY_PING_MS * 4 : RECOVERY_PING_MS; // back off the heartbeat when access refreshes keep coming back retryable, to avoid hammering a flapping token exchange
    this.heartbeatTimer = setTimeout(() => this.zone.run(() => this.probeOnce()), delay);
  }

  doSomethingWhenOnline() {
    const api = this.injector.get(ApiService);
    const savedJwt = getStoredItem('jwt');
    const proceed = () => {
      this.retryableStreak = 0;
      const guard = this.injector.get(UserValidGuardService);
      const snapshot = this.router.routerState.snapshot.root;
      const childSnapshot = snapshot.firstChild;
      if(childSnapshot) {
        guard.checkAll(childSnapshot);
      }
      this.loader.loading(true);
      this.onlineFetchSub?.unsubscribe();
      this.onlineFetchSub = guard.fetchDataDone.subscribe((isDone) => {
        if(isDone) {
          this.isDataSkimRefetch = true;
        }
      });
    };
    // Signed out there is nothing to recheck; the guard's checks would only close any open dialog and sign the visitor out again.
    if(!savedJwt) {
      this.retryableStreak = 0;
      return;
    }
    if(api.getUserInfoOfJwt(savedJwt)?.role !== UserPlatformRole.CUSTOMER) {
      api.checkJwt().then(proceed).catch(() => this.changeOnline(false));
      return;
    }
    api.ensureValidAccess().then(outcome => {
      if(outcome === 'ok') return proceed();
      if(outcome === 'dead') {
        api.signOut();
        this.loader.loading(true, {type: 'error', message: this.lang.transform('exp.session')});
        return;
      }
      this.retryableStreak++; // retryable → back offline without signing out; scheduleHeartbeat backs off once this streaks past 2
      this.changeOnline(false);
    });
  }

  doSomethingWhenOffline() {
    const updateClaimsDates = getStoredItem('updateClaims') || {};

    if(updateClaimsDates.lastSkim) {
      delete updateClaimsDates.lastSkim;
      setStoredItem('updateClaims', updateClaimsDates);
    }

    if(!getStoredItem('jwt')) {
      return;
    }
    const guard = this.injector.get(UserValidGuardService);
    const snapshot = this.router.routerState.snapshot.root;
    const childSnapshot = snapshot.firstChild;
    if(childSnapshot) {
      guard.checkAll(childSnapshot);
    }
  }

  ngOnDestroy() {
    clearTimeout(this.heartbeatTimer);
    this.networkListener?.remove();
    this.appStateListener?.remove();
    this.onlineFetchSub?.unsubscribe();
  }

  loaderOffline() {
    this.zone.run(() => this.loader.loading(true, {type: 'warn', message: this.lang.transform(this.hasInterface ? 'no.connection.server' : 'no.connection'), custom: {icon: 'wifi'}}));
  }
}
